import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { db } from '../../firebaseConfig';
import { collection, addDoc } from 'firebase/firestore';
import './index.css';

const amenitiesList = [
  'Heating', 'Flat Screen TV', 'Free Wi-Fi', 'Private Parking',
  'King Size Bed', 'Single Bed', 'Hair Dryer', 'Work Desk & Chair',
  'Streaming Access', 'Free Toiletries', 'Street Parking',
  'Hot Water', 'Towels', 'Iron & Board'
];

const VillaForm = () => {
  const { register, handleSubmit, formState: { errors }, watch, reset, control } = useForm({
    defaultValues: {
      title: '',
      location: '',
      details: '',
      description: '',
      ldescription: '',
      googleMapLink: '',
      bedrooms: '',
      bathrooms: '',
      basePricePerNight: '',
      additionalGuestCost: '',
      securityDeposit: '',
      vatGst: '',
      maxGuests: '',
      petsAllowed: false,
      amenities: [],
      availableServices: '',
      paymentStructure: '',
      cancellationPolicy: '',
      others: '',
      availableDates: '',
      distances: [{ place: '', time: '' }],
      imageUrls: ['', '']
    }
  });
  const [currentStep, setCurrentStep] = useState(1);

  const watchedFields = watch();

  const onSubmit = async (data) => {
    try {
      const filteredDistances = data.distances.filter(d => d.place && d.time);
      const filteredImageUrls = data.imageUrls.filter(url => url.trim() !== '');
      
      await addDoc(collection(db, 'villas'), {
        ...data,
        distances: filteredDistances,
        imageUrls: filteredImageUrls,
        timestamp: new Date()
      });
      alert('Villa added successfully!');
      reset();
      setCurrentStep(1);
    } catch (error) {
      console.error('Error uploading villa:', error);
      alert('Upload failed. Check console for details.');
    }
  };

  const addDistanceField = () => {
    const distances = watch('distances');
    return {
      ...watchedFields,
      distances: [...distances, { place: '', time: '' }]
    };
  };

  const removeDistanceField = (index) => {
    const distances = watch('distances');
    if (distances.length > 1) {
      return {
        ...watchedFields,
        distances: distances.filter((_, i) => i !== index)
      };
    }
    return watchedFields;
  };

  const nextStep = () => {
    setCurrentStep((prev) => Math.min(prev + 1, 7));
  };

  const prevStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const renderStepIndicator = () => (
    <div className="step-indicator mb-4">
      <div className="progress-line">
        <div 
          className="progress-fill" 
          style={{ width: `${((currentStep - 1) / 6) * 100}%` }}
        ></div>
      </div>
      <div className="d-flex justify-content-between">
        {['Basic Info', 'Details', 'Pricing', 'Amenities', 'Distances', 'Images', 'Review'].map((label, index) => (
          <div
            key={index}
            className={`step text-center ${currentStep >= index + 1 ? 'active' : ''}`}
          >
            <div className="step-circle">
              {index + 1}
            </div>
            <span className="step-label">{label}</span>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="villa-form-container min-vh-100 d-flex align-items-center justify-content-center py-5">
      <div className="containerVilla col-md-8 col-lg-6">
        <h2 className="form-titleVilla text-center mb-4">Add New Villa</h2>
        {renderStepIndicator()}
        <div className="villa-form p-4 bg-white rounded shadow">
          {currentStep === 1 && (
            <div className="form-step mb-4">
              <div className="mb-3">
                <label className="form-label">Villa Title</label>
                <input
                  {...register('title', { required: 'Villa title is required' })}
                  className={`form-control ${errors.title ? 'is-invalid' : ''}`}
                  placeholder="Enter villa title (e.g., Haiwatha By The Brook)"
                />
                {errors.title && <div className="invalid-feedback">{errors.title.message}</div>}
              </div>
              <div className="mb-3">
                <label className="form-label">Location</label>
                <input
                  {...register('location', { required: 'Location is required' })}
                  className={`form-control ${errors.location ? 'is-invalid' : ''}`}
                  placeholder="Enter location (e.g., Ooty, Tamil Nadu)"
                />
                {errors.location && <div className="invalid-feedback">{errors.location.message}</div>}
              </div>
              <div className="mb-3">
                <label className="form-label">Google Map Link</label>
                <input
                  {...register('googleMapLink')}
                  className="form-control"
                  placeholder="Enter Google Map link"
                />
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div className="form-step mb-4">
              <div className="mb-3">
                <label className="form-label">Details</label>
                <input
                  {...register('details', { required: 'Details are required' })}
                  className={`form-control ${errors.details ? 'is-invalid' : ''}`}
                  placeholder="Enter details (e.g., 12-14 Guests • 6 Bedrooms • 6.5 Bathrooms)"
                />
                {errors.details && <div className="invalid-feedback">{errors.details.message}</div>}
              </div>
              <div className="mb-3">
                <label className="form-label">Short Description</label>
                <textarea
                  {...register('description', { required: 'Short description is required' })}
                  className={`form-control ${errors.description ? 'is-invalid' : ''}`}
                  placeholder="Enter short description"
                  rows="3"
                />
                {errors.description && <div className="invalid-feedback">{errors.description.message}</div>}
              </div>
              <div className="mb-3">
                <label className="form-label">Long Description</label>
                <textarea
                  {...register('ldescription', { required: 'Long description is required' })}
                  className={`form-control ${errors.ldescription ? 'is-invalid' : ''}`}
                  placeholder="Enter detailed description"
                  rows="6"
                />
                {errors.ldescription && <div className="invalid-feedback">{errors.ldescription.message}</div>}
              </div>
              <div className="mb-3">
                <label className="form-label">Number of Bedrooms</label>
                <input
                  type="number"
                  {...register('bedrooms', { 
                    required: 'Number of bedrooms is required', 
                    min: { value: 1, message: 'Must have at least one bedroom' }
                  })}
                  className={`form-control ${errors.bedrooms ? 'is-invalid' : ''}`}
                  placeholder="Enter number of bedrooms"
                />
                {errors.bedrooms && <div className="invalid-feedback">{errors.bedrooms.message}</div>}
              </div>
              <div className="mb-3">
                <label className="form-label">Number of Bathrooms</label>
                <input
                  type="number"
                  {...register('bathrooms', { 
                    required: 'Number of bathrooms is required', 
                    min: { value: 1, message: 'Must have at least one bathroom' }
                  })}
                  className={`form-control ${errors.bathrooms ? 'is-invalid' : ''}`}
                  placeholder="Enter number of bathrooms"
                />
                {errors.bathrooms && <div className="invalid-feedback">{errors.bathrooms.message}</div>}
              </div>
            </div>
          )}

          {currentStep === 3 && (
            <div className="form-step mb-4">
              <div className="mb-3">
                <label className="form-label">Base Price per Night (₹)</label>
                <input
                  type="number"
                  {...register('basePricePerNight', { 
                    required: 'Base price is required', 
                    min: { value: 0, message: 'Price cannot be negative' }
                  })}
                  className={`form-control ${errors.basePricePerNight ? 'is-invalid' : ''}`}
                  placeholder="Enter base price per night"
                />
                {errors.basePricePerNight && <div className="invalid-feedback">{errors.basePricePerNight.message}</div>}
              </div>
              <div className="mb-3">
                <label className="form-label">Additional Guest Cost (₹)</label>
                <input
                  type="number"
                  {...register('additionalGuestCost', { 
                    required: 'Additional guest cost is required', 
                    min: { value: 0, message: 'Cost cannot be negative' }
                  })}
                  className={`form-control ${errors.additionalGuestCost ? 'is-invalid' : ''}`}
                  placeholder="Enter additional guest cost"
                />
                {errors.additionalGuestCost && <div className="invalid-feedback">{errors.additionalGuestCost.message}</div>}
              </div>
              <div className="mb-3">
                <label className="form-label">Security Deposit (₹)</label>
                <input
                  type="number"
                  {...register('securityDeposit', { 
                    required: 'Security deposit is required', 
                    min: { value: 0, message: 'Deposit cannot be negative' }
                  })}
                  className={`form-control ${errors.securityDeposit ? 'is-invalid' : ''}`}
                  placeholder="Enter security deposit"
                />
                {errors.securityDeposit && <div className="invalid-feedback">{errors.securityDeposit.message}</div>}
              </div>
              <div className="mb-3">
                <label className="form-label">VAT/GST (%)</label>
                <input
                  type="number"
                  step="0.01"
                  {...register('vatGst', { 
                    required: 'VAT/GST percentage is required', 
                    min: { value: 0, message: 'VAT/GST cannot be negative' },
                    max: { value: 100, message: 'VAT/GST cannot exceed 100%' }
                  })}
                  className={`form-control ${errors.vatGst ? 'is-invalid' : ''}`}
                  placeholder="Enter VAT/GST percentage (e.g., 18)"
                />
                {errors.vatGst && <div className="invalid-feedback">{errors.vatGst.message}</div>}
              </div>
              <div className="mb-3">
                <label className="form-label">Max Guests</label>
                <input
                  type="number"
                  {...register('maxGuests', { 
                    required: 'Max guests is required', 
                    min: { value: 1, message: 'Must allow at least one guest' }
                  })}
                  className={`form-control ${errors.maxGuests ? 'is-invalid' : ''}`}
                  placeholder="Enter max guests"
                />
                {errors.maxGuests && <div className="invalid-feedback">{errors.maxGuests.message}</div>}
              </div>
              <div className="mb-3">
                <label className="form-label">Available Dates</label>
                <input
                  {...register('availableDates', { required: 'Available dates are required' })}
                  className={`form-control ${errors.availableDates ? 'is-invalid' : ''}`}
                  placeholder="e.g., MM/DD/YYYY - MM/DD/YYYY"
                />
                {errors.availableDates && <div className="invalid-feedback">{errors.availableDates.message}</div>}
              </div>
              <div className="mb-3 form-check">
                <input
                  type="checkbox"
                  {...register('petsAllowed')}
                  className="form-check-input"
                  id="petsAllowed"
                />
                <label className="form-check-label" htmlFor="petsAllowed">Pets Allowed</label>
              </div>
            </div>
          )}

          {currentStep === 4 && (
            <div className="form-step mb-4">
              <fieldset className="border p-3 rounded mb-3">
                <legend className="form-label">Select Amenities</legend>
                <div className="row">
                  {amenitiesList.map((amenity, index) => (
                    <div key={index} className="col-6 mb-2">
                      <div className="form-check">
                        <input
                          type="checkbox"
                          {...register('amenities')}
                          value={amenity}
                          className="form-check-input"
                          id={`amenity-${index}`}
                        />
                        <label className="form-check-label" htmlFor={`amenity-${index}`}>{amenity}</label>
                      </div>
                    </div>
                  ))}
                </div>
              </fieldset>
              <div className="mb-3">
                <label className="form-label">Available Services</label>
                <textarea
                  {...register('availableServices')}
                  className="form-control"
                  placeholder="Enter available services (e.g., cleaning, airport transfer)"
                  rows="3"
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Payment Structure</label>
                <textarea
                  {...register('paymentStructure')}
                  className="form-control"
                  placeholder="Enter payment structure (e.g., 50% upfront, 50% on arrival)"
                  rows="3"
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Cancellation Policy</label>
                <textarea
                  {...register('cancellationPolicy')}
                  className="form-control"
                  placeholder="Enter cancellation policy"
                  rows="3"
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Other Information</label>
                <textarea
                  {...register('others')}
                  className="form-control"
                  placeholder="Enter any other information"
                  rows="3"
                />
              </div>
            </div>
          )}

          {currentStep === 5 && (
            <div className="form-step mb-4">
              <div className="mb-3">
                <label className="form-label">Nearby Distances</label>
                <Controller
                  name="distances"
                  control={control}
                  render={({ field }) => (
                    <div>
                      {field.value.map((distance, index) => (
                        <div key={index} className="distance-input-group mb-2 d-flex align-items-center">
                          <input
                            type="text"
                            value={distance.place}
                            onChange={(e) => {
                              const newDistances = [...field.value];
                              newDistances[index].place = e.target.value;
                              field.onChange(newDistances);
                            }}
                            className="form-control me-2"
                            placeholder="Place (e.g., Ooty)"
                          />
                          <input
                            type="text"
                            value={distance.time}
                            onChange={(e) => {
                              const newDistances = [...field.value];
                              newDistances[index].time = e.target.value;
                              field.onChange(newDistances);
                            }}
                            className="form-control me-2"
                            placeholder="Time (e.g., 20 mins)"
                          />
                          {field.value.length > 1 && (
                            <button
                              type="button"
                              className="btn btn-danger btn-sm"
                              onClick={() => field.onChange(removeDistanceField(index).distances)}
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      ))}
                      <button
                        type="button"
                        className="btn btn-outline-primary mt-2"
                        onClick={() => field.onChange(addDistanceField().distances)}
                      >
                        Add Distance
                      </button>
                    </div>
                  )}
                />
              </div>
            </div>
          )}

          {currentStep === 6 && (
            <div className="form-step mb-4">
              <div className="mb-3">
                <label className="form-label">Enter up to 2 Image URLs</label>
                {[0, 1].map((index) => (
                  <div key={index} className="mb-2">
                    <input
                      type="url"
                      {...register(`imageUrls.${index}`, {
                        pattern: {
                          value: /^(https?:\/\/.*\.(?:png|jpg|jpeg|gif|webp))/i,
                          message: 'Please enter a valid image URL'
                        }
                      })}
                      className={`form-control ${errors.imageUrls?.[index] ? 'is-invalid' : ''}`}
                      placeholder={`Image URL ${index + 1}`}
                    />
                    {errors.imageUrls?.[index] && (
                      <div className="invalid-feedback">{errors.imageUrls[index].message}</div>
                    )}
                    {watchedFields.imageUrls[index] && (
                      <img
                        src={watchedFields.imageUrls[index]}
                        alt={`Preview ${index + 1}`}
                        className="image-thumbnail rounded mt-2"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {currentStep === 7 && (
            <div className="form-step mb-4">
              <h3 className="review-title mb-3">Review Your Villa Details</h3>
              <div className="review-section mb-3">
                <h5>Basic Info</h5>
                <p><strong>Title:</strong> {watchedFields.title || 'N/A'}</p>
                <p><strong>Location:</strong> {watchedFields.location || 'N/A'}</p>
                <p><strong>Google Map Link:</strong> {watchedFields.googleMapLink || 'N/A'}</p>
              </div>
              <div className="review-section mb-3">
                <h5>Details</h5>
                <p><strong>Details:</strong> {watchedFields.details || 'N/A'}</p>
                <p><strong>Short Description:</strong> {watchedFields.description || 'N/A'}</p>
                <p><strong>Long Description:</strong> {watchedFields.ldescription || 'N/A'}</p>
                <p><strong>Bedrooms:</strong> {watchedFields.bedrooms || 'N/A'}</p>
                <p><strong>Bathrooms:</strong> {watchedFields.bathrooms || 'N/A'}</p>
              </div>
              <div className="review-section mb-3">
                <h5>Pricing</h5>
                <p><strong>Base Price per Night:</strong> ₹{watchedFields.basePricePerNight || '0'}</p>
                <p><strong>Additional Guest Cost:</strong> ₹{watchedFields.additionalGuestCost || '0'}</p>
                <p><strong>Security Deposit:</strong> ₹{watchedFields.securityDeposit || '0'}</p>
                <p><strong>VAT/GST:</strong> {watchedFields.vatGst || '0'}%</p>
                <p><strong>Max Guests:</strong> {watchedFields.maxGuests || '0'}</p>
                <p><strong>Available Dates:</strong> {watchedFields.availableDates || 'N/A'}</p>
                <p><strong>Pets Allowed:</strong> {watchedFields.petsAllowed ? 'Yes' : 'No'}</p>
              </div>
              <div className="review-section mb-3">
                <h5>Amenities & Services</h5>
                <p><strong>Amenities:</strong> {watchedFields.amenities.length > 0 ? watchedFields.amenities.join(', ') : 'None'}</p>
                <p><strong>Available Services:</strong> {watchedFields.availableServices || 'N/A'}</p>
                <p><strong>Payment Structure:</strong> {watchedFields.paymentStructure || 'N/A'}</p>
                <p><strong>Cancellation Policy:</strong> {watchedFields.cancellationPolicy || 'N/A'}</p>
                <p><strong>Other Information:</strong> {watchedFields.others || 'N/A'}</p>
              </div>
              <div className="review-section mb-3">
                <h5>Distances</h5>
                {watchedFields.distances.filter(d => d.place && d.time).length > 0 ? (
                  <ul>
                    {watchedFields.distances
                      .filter(d => d.place && d.time)
                      .map((distance, index) => (
                        <li key={index}>{distance.place}: {distance.time}</li>
                      ))}
                  </ul>
                ) : (
                  <p>No distances provided</p>
                )}
              </div>
              <div className="review-section">
                <h5>Images</h5>
                <p><strong>Image URLs:</strong> {watchedFields.imageUrls.filter(url => url.trim() !== '').length} image(s)</p>
                {watchedFields.imageUrls.some(url => url.trim() !== '') && (
                  <div className="image-preview d-flex flex-wrap gap-2">
                    {watchedFields.imageUrls
                      .filter(url => url.trim() !== '')
                      .map((url, index) => (
                        <img
                          key={index}
                          src={url}
                          alt={`Preview ${index + 1}`}
                          className="image-thumbnail rounded"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      ))}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="form-navigation d-flex justify-content-between mt-4">
            {currentStep > 1 && (
              <button type="button" onClick={prevStep} className="btn btn-outline-secondary">
                Previous
              </button>
            )}
            {currentStep < 7 && (
              <button type="button" onClick={nextStep} className="btn btn-primary">
                Next
              </button>
            )}
            {currentStep === 7 && (
              <button type="button" onClick={handleSubmit(onSubmit)} className="btn btn-success">
                Submit Villa
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VillaForm;